import { DiffType } from '@ardoq/data-model';
import { ARDOQ_DEFAULT_FONT_FAMILY } from '@ardoq/typography';
import { Font, Size, TextMeasurePolicy, TextWrapping } from '@ardoq/yfiles';
import { MODERNIZED_BLOCK_DIAGRAM_NODE_WIDTH } from './modernized/consts';
import {
  MODERNIZED_BLOCK_DIAGRAM_LEAF_NODE_LABEL_MAX_HEIGHT,
  MODERNIZED_BLOCK_DIAGRAM_LEAF_NODE_LABEL_WIDTH,
} from 'tabview/blockDiagram/view/yFilesExtensions/modernized/consts';
import { OTHER_LABEL_TEXT_ELEMENT_HORIZONTAL_PADDING } from 'tabview/blockDiagram/view/yFilesExtensions/labels/consts';
import {
  MODERNIZED_BLOCK_DIAGRAM_FONT_SIZE,
  MODERNIZED_BLOCK_DIAGRAM_LINE_HEIGHT,
} from '@ardoq/graph-labeling';

export const DATA_RENDER_HASH = 'data-render-hash';
export const NODE_HEIGHT = 72;
export const NODE_WIDTH = 128.5;
export const SPACE_FACTOR = NODE_WIDTH / 8;
export const CONTEXT_HIGHLIGHT_PADDING = 30;
export const HALO_PADDING = 8;
export const CONTEXT_HIGHLIGHT_STROKE = 1;
export const LABEL_HORIZONTAL_PADDING = 30;
export const LABEL_VERTICAL_PADDING = 6;
export const SELECTION_RADIUS_DIFF = 6;
export const SELECTION_STROKE_WIDTH = 4;
export const NODE_HEIGHT_WIDTH_SELECTION =
  NODE_HEIGHT + 2 * SELECTION_RADIUS_DIFF + SELECTION_STROKE_WIDTH;

export const IMAGE_WIDTH = 72;
export const IMAGE_HEIGHT = 72;
export const PLACEHOLDER_RADIUS = 72 / 2;

export const URL_BOX_ICON_CLASS_NAME = 'yfiles-url-box-icon';

export const URL_ICON_MARGIN = 8;
export const URL_ICON_SIZE = 24;

export const visualDiffModes = new Set([
  DiffType.ADDED,
  DiffType.REMOVED,
  DiffType.CHANGED,
  DiffType.UNCHANGED,
]);

/** line spacing is added between `<tspan>` elements. it is a value relative to the font size. sensible values are between zero and one, where 1 means double-spaced lines. */
export const MODERNIZED_BLOCK_DIAGRAM_LINE_SPACING =
  MODERNIZED_BLOCK_DIAGRAM_LINE_HEIGHT / MODERNIZED_BLOCK_DIAGRAM_FONT_SIZE - 1;

export const BLOCK_DIAGRAM_NODE_LABEL_FONT = new Font({
  fontFamily: ARDOQ_DEFAULT_FONT_FAMILY,
  fontSize: 32,
  fontWeight: 'normal',
});
export const MODERNIZED_BLOCK_DIAGRAM_NODE_LABEL_FONT = new Font({
  fontFamily: ARDOQ_DEFAULT_FONT_FAMILY,
  fontSize: MODERNIZED_BLOCK_DIAGRAM_FONT_SIZE,
  fontWeight: '500',
  lineSpacing: MODERNIZED_BLOCK_DIAGRAM_LINE_SPACING,
});

export const BLOCK_DIAGRAM_EDGE_LABEL_FONT = new Font({
  fontFamily: ARDOQ_DEFAULT_FONT_FAMILY,
  fontSize: 24,
  fontWeight: 'normal',
});
export const MODERNIZED_BLOCK_DIAGRAM_EDGE_LABEL_FONT = new Font({
  fontFamily: ARDOQ_DEFAULT_FONT_FAMILY,
  fontSize: MODERNIZED_BLOCK_DIAGRAM_FONT_SIZE,
  fontWeight: 'normal',
});
export const BLOCK_DIAGRAM_OTHER_LABEL_FONT = new Font(
  ARDOQ_DEFAULT_FONT_FAMILY,
  28
);
export const MODERNIZED_BLOCK_DIAGRAM_OTHER_LABEL_FONT = new Font({
  fontFamily: ARDOQ_DEFAULT_FONT_FAMILY,
  fontSize: MODERNIZED_BLOCK_DIAGRAM_FONT_SIZE,
  fontWeight: '400',
  lineSpacing: MODERNIZED_BLOCK_DIAGRAM_LINE_SPACING,
});
export const BLOCK_DIAGRAM_OTHER_LABEL_MAX_SIZE = new Size(
  300,
  8 * BLOCK_DIAGRAM_OTHER_LABEL_FONT.fontSize
);
export const MODERNIZED_BLOCK_DIAGRAM_OTHER_LABEL_MAX_SIZE = new Size(
  MODERNIZED_BLOCK_DIAGRAM_NODE_WIDTH -
    2 * OTHER_LABEL_TEXT_ELEMENT_HORIZONTAL_PADDING,
  8 * MODERNIZED_BLOCK_DIAGRAM_OTHER_LABEL_FONT.fontSize
);
const MAX_WIDTH = 300;

export const BLOCK_DIAGRAM_EDGE_LABEL_MAX_SIZE = new Size(
  MAX_WIDTH,
  8 * BLOCK_DIAGRAM_EDGE_LABEL_FONT.fontSize
);
export const MODERNIZED_BLOCK_DIAGRAM_EDGE_LABEL_MAX_SIZE = new Size(
  MAX_WIDTH,
  8 * MODERNIZED_BLOCK_DIAGRAM_EDGE_LABEL_FONT.fontSize
);
export const BLOCK_DIAGRAM_NAME_LABEL_MAX_SIZE = new Size(
  300,
  8 * BLOCK_DIAGRAM_NODE_LABEL_FONT.fontSize
);
export const MODERNIZED_BLOCK_DIAGRAM_NAME_LABEL_MAX_SIZE = new Size(
  MODERNIZED_BLOCK_DIAGRAM_LEAF_NODE_LABEL_WIDTH,
  MODERNIZED_BLOCK_DIAGRAM_LEAF_NODE_LABEL_MAX_HEIGHT
);
export const BLOCK_DIAGRAM_LABEL_TEXT_PARAMS = {
  maximumSize: BLOCK_DIAGRAM_NAME_LABEL_MAX_SIZE,
  wrapping: TextWrapping.WRAP_WORD_ELLIPSIS,
  measurePolicy: TextMeasurePolicy.SVG,
};
export const MODERNIZED_BLOCK_DIAGRAM_LABEL_TEXT_PARAMS = {
  maximumSize: MODERNIZED_BLOCK_DIAGRAM_NAME_LABEL_MAX_SIZE,
  wrapping: TextWrapping.WRAP_WORD_ELLIPSIS,
  measurePolicy: TextMeasurePolicy.SVG,
};

export const BLOCK_DIAGRAM_LABEL_TEXT_PARAMS_INFINITE_HEIGHT = {
  ...BLOCK_DIAGRAM_LABEL_TEXT_PARAMS,
  maximumSize: new Size(
    BLOCK_DIAGRAM_LABEL_TEXT_PARAMS.maximumSize.width,
    Infinity
  ),
};
export const MODERNIZED_BLOCK_DIAGRAM_LABEL_TEXT_PARAMS_INFINITE_HEIGHT = {
  ...MODERNIZED_BLOCK_DIAGRAM_LABEL_TEXT_PARAMS,
  maximumSize: new Size(
    MODERNIZED_BLOCK_DIAGRAM_LABEL_TEXT_PARAMS.maximumSize.width,
    Infinity
  ),
};

export const MODERNIZED_BLOCK_DIAGRAM_EDGE_LABEL_MAX_WIDTH = 300;
